import { useQuery } from '@apollo/client';
import loadable from '@loadable/component';
import { type FunctionComponent } from 'react';
import {
	type CustomerIsAuthenticatedQuery,
	type CustomerIsAuthenticatedQueryVariables
} from '../../../client/__generated__/graphql-client-types';
import { useCustomer } from '../../hooks/apollo/customer/customer.hooks';
import { useRouterLocation } from '../../hooks/routing/location.hook';
import { useActiveSessionRedirect } from '../../hooks/session/session.hooks';
import { CUSTOMER_IS_AUTHENTICATED } from '../../queries/customer/customer.queries';
import { PageLoading } from '../common-components/loading/loading.component';
import { LayoutController, type LayoutControllerProps } from '../layouts/layout.controller';

const LoadableChallenge = loadable(() => import(/* webpackChunkName: "authentication-challenge" */ './challenge/challenge.component'), {
	resolveComponent: ({ Challenge }) => Challenge
});

export type AuthenticatedRouteProps = LayoutControllerProps & { isSoftLogin?: boolean };
export const AuthenticatedRoute: FunctionComponent<AuthenticatedRouteProps> = ({ children, layoutType, isSoftLogin = false }) => {
	const { pathname } = useRouterLocation();
	const { data, loading } = useQuery<CustomerIsAuthenticatedQuery, CustomerIsAuthenticatedQueryVariables>(CUSTOMER_IS_AUTHENTICATED, {
		skip: isSoftLogin
	});
	const isAuthenticated = data?.customer?.isAuthenticated;
	const { hasAccount } = useCustomer();

	const check = isSoftLogin ? hasAccount : isAuthenticated;

	// don't check session if it's a soft login, or if we know the user is not authenticated
	useActiveSessionRedirect({ returnToRoute: pathname, enablePolling: isSoftLogin === true ? false : check === true });

	// while loading render the route, but with a loading indicator rather than children
	if (loading) {
		return (
			<LayoutController layoutType={layoutType}>
				<PageLoading />
			</LayoutController>
		);
	}
	return check ? children : <LoadableChallenge layoutType={layoutType} />;
};
